import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChartBar, ChartColumn, ChartPie, ChartLine, BarChart3, Settings2, Table2 } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import Tooltip from '../tooltip';

interface ChartSeries {
  name: string;
  data: number[];
  stack?: string;
}

interface UniversalChartData {
  type: 'line' | 'bar' | 'column' | 'stackedColumn' | 'pie';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  stacking?: 'normal';
  showLegend?: boolean;
  colors?: string[];
}

interface ChartTypeOption {
  type: string;
  icon: LucideIcon;
  label: string;
  color: string;
  isEdit?: boolean;
}

interface ChartSwitcherProps {
  defaultType: string | null | undefined;
  currentType: string;
  data?: UniversalChartData;
  onTypeChange: (newType: string) => void;
  isTableView?: boolean;
  onEditClick?: () => void;
  showTableOption?: boolean;
}

const ChartSwitcher: React.FC<ChartSwitcherProps> = ({
  defaultType: _defaultType,
  currentType,
  data: _data,
  onTypeChange,
  isTableView = false,
  onEditClick,
  showTableOption = false
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);

  const getAvailableChartTypes = (): ChartTypeOption[] => {
    const types: ChartTypeOption[] = [];

    if (showTableOption) {
      types.push({
        type: 'table',
        icon: Table2,
        label: 'Table',
        color: 'text-primary-text'
      });
    }

    types.push({
      type: 'barchart',
      icon: ChartBar,
      label: 'Bar Chart',
      color: 'text-primary-text'
    });

    types.push({
      type: 'columnchart',
      icon: ChartColumn,
      label: 'Column Chart',
      color: 'text-primary-text'
    });

    types.push({
      type: 'linechart',
      icon: ChartLine,
      label: 'Line Chart',
      color: 'text-primary-text'
    });

    types.push({
      type: 'piechart',
      icon: ChartPie,
      label: 'Pie Chart',
      color: 'text-primary-text'
    });

    types.push({
      type: 'edit',
      icon: Settings2,
      label: 'Edit Chart',
      color: 'text-primary-text',
      isEdit: true
    });

    return types;
  };

  const availableTypes: ChartTypeOption[] = getAvailableChartTypes();

  const handleTypeChange = (chartType: ChartTypeOption) => {
    if (chartType.isEdit && onEditClick) {
      onEditClick();
      setShowOptions(false);
      return;
    }
    onTypeChange(chartType.type);
    setShowOptions(false);
  };

  // Map current chart type to display type for comparison  
  const normalizeType = (type: string): string => {
    // No normalization needed since we're using the exact same names
    return type;
  };

  const normalizedCurrentType = normalizeType(currentType);

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && showOptions) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          right: window.innerWidth - rect.right + window.scrollX
        });
      }
    };

    if (showOptions) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [showOptions]);

  const dropdownContent = showOptions && dropdownPosition ? (
    <>
      <div className="fixed inset-0 z-[9998]" onClick={() => setShowOptions(false)} />
      <div
        className="fixed bg-white border border-gray-200 md:rounded-3xl rounded-[12px] shadow-xl min-w-[200px] z-[9999]"
        style={{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`
        }}
      >
        <div className="p-2">
          <div className="space-y-1">
            {availableTypes.map((chartType, index) => {
              const Icon = chartType.icon;
              const isActive = !chartType.isEdit && normalizedCurrentType === chartType.type;

              return (
                <button
                  key={`${chartType.type}-${index}`}
                  onClick={() => handleTypeChange(chartType)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 text-sm md:rounded-3xl rounded-[12px] transition-colors
                    ${chartType.isEdit
                      ? 'hover:bg-gray-100 text-gray-600 border-t border-gray-100 mt-1 pt-2'
                      : isActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'hover:bg-blue-50 text-gray-700 border-blue-200'
                    }
                  `}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-primary' : chartType.color}`} />
                  <span>{chartType.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className="relative" ref={buttonRef}>
      <Tooltip message="Change chart type" >
        <button
          type="button"
          title="Change chart type"
          onClick={() => setShowOptions(!showOptions)}
          className={`p-2 group flex items-center justify-center rounded-full transition-colors ${showOptions ? 'text-primary' : 'text-tertiary-text hover:bg-secondary-background hover:text-primary'}`}
        >
          <BarChart3 className="w-5 h-5" strokeWidth={1.75} />
        </button>
      </Tooltip>

      {dropdownContent && typeof document !== 'undefined' && createPortal(
        dropdownContent,
        document.body
      )}
    </div>
  );
};

export default ChartSwitcher;